import type { ApiClient } from 'grabbitbot-legacy-ui'
import type {
  CoinConfig,
  ExchangeKeyPatch,
  ExchangeKeyView,
  PossibleCoinList,
  QuoteAsset,
  SpotDefaultConfig,
  Strategy,
  StrategyCoin,
  Tag,
} from './tradingTypes'

/** Legacy `V3SpotConfigService.clearCache()` / `V3QuoteAssetService.clearCache()`
 * pattern `/v3/spot/config/.*` — every config write drops every cached
 * config read, exactly like legacy. */
const CONFIG_CACHE = /\/v3\/spot\/config\//
const DEFAULT_LIST_CACHE = /\/v3\/spot\/config\/default/
/** Legacy `V3SpotExchangeService.clearCache()` pattern `/v3/spot/exchange/.*`. */
const EXCHANGE_CACHE = /\/v3\/spot\/exchange\//

type Query = Record<string, string | number | boolean | undefined | null>

/**
 * Spotbot's coin-config search answer is proxied PHP-style: a map whose keys
 * are `0..n-1` arrives as a JSON list, any other map (e.g. keyed by pair)
 * stays an object (B2 contract #5, backend test
 * `test_coin_config_search_shapes_every_item`). The legacy Angular client
 * only ever iterated it as a list; accept both so a keyed map does not crash
 * the page (contract question raised in the B2 portal port report).
 */
export function coinConfigList(result: CoinConfig[] | Record<string, CoinConfig> | null | undefined): CoinConfig[] {
  if (!result) return []
  if (Array.isArray(result)) return result
  if (typeof result === 'object') return Object.values(result)
  return []
}

/** Same PHP-style normalisation for passthrough list answers (tags). */
export function asList<T>(result: T[] | Record<string, T> | null | undefined): T[] {
  if (!result) return []
  if (Array.isArray(result)) return result
  if (typeof result === 'object') return Object.values(result)
  return []
}

/**
 * B2 domain client — ports `V3SpotConfigService`, `V3QuoteAssetService`,
 * `V3SpotStrategyService` and `V3SpotExchangeService`
 * (`src/app/data/api-client/v3/*.ts` in the legacy archive). Trading type is
 * always `spot` (owner 2026-09-26: marginbot is not rewritten).
 */
export function createTradingClient(client: ApiClient) {
  return {
    // --- default config -------------------------------------------------
    /** `V3SpotConfigService.searchDefaultConfigs(true)` — the settings pages
     * always pass `checkExchange=1` so `exchangeKey` carries real presence
     * flags (B1 contract §31). */
    searchDefaultConfigs: (checkExchange = true) =>
      client.get<SpotDefaultConfig[]>('/v3/spot/config/default', {
        query: checkExchange ? { checkExchange: 1 } : undefined,
      }),
    viewDefaultConfig: (exchangeId: string) =>
      client.get<SpotDefaultConfig>('/v3/spot/config/{exchangeId}/default', { params: { exchangeId } }),
    modifyDefaultConfig: (exchangeId: string, data: unknown) =>
      client.patch<SpotDefaultConfig>('/v3/spot/config/{exchangeId}/default', data, {
        params: { exchangeId },
        invalidates: [CONFIG_CACHE],
      }),

    // --- quote assets / possible coins ---------------------------------
    searchQuoteAssets: (exchangeId: string) =>
      client.get<QuoteAsset[]>('/v3/spot/config/{exchangeId}/quote-assets', { params: { exchangeId } }),
    searchPossibleCoins: (exchangeId: string, filter?: Query) =>
      client.get<PossibleCoinList>('/v3/spot/config/{exchangeId}/possible-coins', {
        params: { exchangeId },
        query: filter,
      }),

    // --- coin config ----------------------------------------------------
    searchCoinConfigs: async (exchangeId: string, filter?: Query) =>
      coinConfigList(
        await client.get<CoinConfig[] | Record<string, CoinConfig>>('/v3/spot/config/{exchangeId}/coin', {
          params: { exchangeId },
          query: filter,
        }),
      ),
    /** Legacy appended a random `r` query value to defeat its cache; here the
     * client cache is simply bypassed. */
    getCoinConfig: (exchangeId: string, coin1: string, coin2: string) =>
      client.get<CoinConfig>('/v3/spot/config/{exchangeId}/coin/{coin1}/{coin2}', {
        params: { exchangeId, coin1, coin2 },
        cache: false,
      }),
    modifyCoinConfig: (exchangeId: string, coin1: string, coin2: string, data: unknown) =>
      client.patch<CoinConfig>('/v3/spot/config/{exchangeId}/coin/{coin1}/{coin2}', data, {
        params: { exchangeId, coin1, coin2 },
        invalidates: [CONFIG_CACHE],
      }),
    modifyCoinConfigByTag: (exchangeId: string, tagId: string, data: unknown) =>
      client.patch<unknown>('/v3/spot/config/{exchangeId}/coin/bytag/{tagId}', data, {
        params: { exchangeId, tagId },
        invalidates: [CONFIG_CACHE],
      }),
    subscribeCoinConfig: (
      exchangeId: string,
      coin1: string,
      coin2: string,
      traderId: string,
      strategyId: string,
      data: unknown = {},
    ) =>
      client.patch<CoinConfig>(
        '/v3/spot/config/{exchangeId}/coin/{coin1}/{coin2}/subscribe/{traderId}/{strategyId}',
        data,
        { params: { exchangeId, coin1, coin2, traderId, strategyId }, invalidates: [CONFIG_CACHE] },
      ),
    unsubscribeCoinConfig: (exchangeId: string, coin1: string, coin2: string, data: unknown = {}) =>
      client.patch<CoinConfig>('/v3/spot/config/{exchangeId}/coin/{coin1}/{coin2}/unsubscribe', data, {
        params: { exchangeId, coin1, coin2 },
        invalidates: [CONFIG_CACHE],
      }),
    addTagToCoinConfig: (exchangeId: string, coin1: string, coin2: string, tagId: string) =>
      client.post<CoinConfig>('/v3/spot/config/{exchangeId}/coin/{coin1}/{coin2}/tag/{tagId}', undefined, {
        params: { exchangeId, coin1, coin2, tagId },
        invalidates: [CONFIG_CACHE],
      }),
    removeTagFromCoinConfig: (exchangeId: string, coin1: string, coin2: string, tagId: string) =>
      client.delete<CoinConfig>('/v3/spot/config/{exchangeId}/coin/{coin1}/{coin2}/tag/{tagId}', {
        params: { exchangeId, coin1, coin2, tagId },
        invalidates: [CONFIG_CACHE],
      }),

    // --- tags -----------------------------------------------------------
    searchTags: async (exchangeId: string, filter?: Query) =>
      asList(
        await client.get<Tag[] | Record<string, Tag>>('/v3/spot/config/{exchangeId}/tags', {
          params: { exchangeId },
          query: filter,
        }),
      ),
    getTag: (exchangeId: string, tagId: string) =>
      client.get<Tag>('/v3/spot/config/{exchangeId}/tags/{tagId}', { params: { exchangeId, tagId }, cache: false }),
    createTag: (exchangeId: string, data: { name: string; description?: string }) =>
      client.post<Tag>('/v3/spot/config/{exchangeId}/tags', data, {
        params: { exchangeId },
        invalidates: [CONFIG_CACHE],
      }),
    modifyTag: (exchangeId: string, tagId: string, data: Partial<Tag>) =>
      client.patch<Tag>('/v3/spot/config/{exchangeId}/tags/{tagId}', data, {
        params: { exchangeId, tagId },
        invalidates: [CONFIG_CACHE],
      }),
    deleteTag: (exchangeId: string, tagId: string) =>
      client.delete<unknown>('/v3/spot/config/{exchangeId}/tags/{tagId}', {
        params: { exchangeId, tagId },
        invalidates: [CONFIG_CACHE],
      }),

    // --- strategies -----------------------------------------------------
    searchStrategies: () => client.get<Strategy[]>('/v3/spot/config/strategy'),
    searchMarketplace: () => client.get<Strategy[]>('/v3/spot/config/strategy/marketplace'),
    searchStrategiesByIds: (ids: string[]) =>
      client.get<Strategy[]>('/v3/spot/config/strategy/byids/{ids}', { params: { ids: ids.join(',') } }),
    viewStrategy: (id: string) => client.get<Strategy>('/v3/spot/config/strategy/{id}', { params: { id } }),
    searchStrategyCoins: (id: string) =>
      client.get<StrategyCoin[]>('/v3/spot/config/strategy/{id}/coin', { params: { id } }),

    // --- exchange key (WRITE-ONLY) --------------------------------------
    /** Never cached: legacy appended `t=<now>` for the same reason. The
     * answer carries presence flags only (see `ExchangeKeyView`). */
    viewExchangeKey: (exchangeId: string) =>
      client.get<ExchangeKeyView>('/v3/spot/exchange/{exchangeId}/key', { params: { exchangeId }, cache: false }),
    /** `data` must contain ONLY the fields the user typed — see
     * `buildExchangeKeyPatch` in the exchange settings page. */
    modifyExchangeKey: (exchangeId: string, data: ExchangeKeyPatch) =>
      client.patch<ExchangeKeyView>('/v3/spot/exchange/{exchangeId}/key', data, {
        params: { exchangeId },
        invalidates: [EXCHANGE_CACHE, DEFAULT_LIST_CACHE],
      }),
  }
}

export type TradingClient = ReturnType<typeof createTradingClient>
